/**
 * Importing a file of mappings: pick a file, choose merge or replace, run the
 * import against the screen's endpoint and read the outcome. `useImportFlow`
 * owns the whole sequence; a screen wires its `start` to an Import action and
 * renders its `dialog`.
 */

import React, { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControlLabel, Checkbox, Typography, Alert, Box, Stack,
  useMediaQuery, useTheme,
} from '@mui/material';
import { httpClient, HttpError } from '@/utils/httpClient';
import { readFile, type FileFormat } from '@/utils/jsonFileUtils';

export type ImportResult = {
  mode: 'Merge' | 'Replace';
  total: number;
  created: number;
  updated: number;
  skipped: number;
  deleted: number;
  errors: Array<{ index: number; message: string }>;
};

const MAX_LISTED_ERRORS = 50;

type ImportDialogProps = {
  open: boolean;
  /** How many records the file holds. */
  recordCount: number;
  replace: boolean;
  onReplaceChange: (v: boolean) => void;
  importing: boolean;
  result: ImportResult | null;
  onClose: () => void;
  onRun: () => void;
};

const ResultLine: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

export const ImportDialog: React.FC<ImportDialogProps> = ({
  open, recordCount, replace, onReplaceChange, importing, result, onClose, onRun,
}) => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isPhone}>
      <DialogTitle>{t('common.importDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">{t('common.importDialog.records', { smart_count: recordCount })}</Typography>

          {!result && (
            <>
              <FormControlLabel
                control={<Checkbox checked={replace} onChange={(e) => onReplaceChange(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2">{t('common.importDialog.replace')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('common.importDialog.replaceHint')}</Typography>
                  </Box>
                }
              />
              <Alert severity={replace ? 'warning' : 'info'}>
                {t(replace ? 'common.importDialog.replaceWarning' : 'common.importDialog.mergeInfo')}
              </Alert>
            </>
          )}

          {result && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">{t('common.importDialog.resultTitle')}</Typography>
              <ResultLine label={t('common.importDialog.total')} value={result.total} />
              <ResultLine label={t('common.importDialog.created')} value={result.created} />
              <ResultLine label={t('common.importDialog.updated')} value={result.updated} />
              <ResultLine label={t('common.importDialog.skipped')} value={result.skipped} />
              {result.deleted > 0 && <ResultLine label={t('common.importDialog.deleted')} value={result.deleted} />}
            </Stack>
          )}

          {result && result.errors?.length > 0 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('common.importDialog.errors', { smart_count: result.errors.length })}
              </Alert>
              <Box component="ul" sx={{ m: 0, pl: 2.5, fontFamily: theme.custom.text.mono }}>
                {result.errors.slice(0, MAX_LISTED_ERRORS).map((e) => (
                  <Typography component="li" variant="body2" key={e.index}>
                    {t('common.importDialog.errorRow', { row: e.index + 1, message: e.message })}
                  </Typography>
                ))}
              </Box>
              {result.errors.length > MAX_LISTED_ERRORS && (
                <Typography variant="body2" color="text.secondary">
                  {t('common.more', { count: result.errors.length - MAX_LISTED_ERRORS })}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={importing}>
          {t(result ? 'ra.action.close' : 'ra.action.cancel')}
        </Button>
        {!result && (
          <Button variant="contained" color={replace ? 'error' : 'primary'} onClick={onRun} disabled={importing}>
            {t(importing ? 'common.importDialog.importing' : replace ? 'common.importDialog.runReplace' : 'common.importDialog.run')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export interface ImportFlowOptions<T> {
  /** POST endpoint that takes `{ mode, items }` and answers an `ImportResult`. */
  endpoint: string;
  /** Turns the file's content into the records to send, or null when it is not recognised. */
  parse: (data: unknown, format: FileFormat) => T[] | null;
}

/** The import sequence of a screen: `start` opens the file picker, `dialog` is rendered once. */
export const useImportFlow = <T,>({ endpoint, parse }: ImportFlowOptions<T>) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [items, setItems] = useState<T[] | null>(null);
  const [replace, setReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const start = async () => {
    const { data, format } = await readFile<unknown>();
    if (!data) { notify('common.importDialog.invalidFile', { type: 'error' }); return; }
    const parsed = parse(data, format);
    if (!parsed || parsed.length === 0) { notify('common.importDialog.emptyFile', { type: 'error' }); return; }
    setItems(parsed);
    setReplace(false);
    setResult(null);
  };

  const run = async () => {
    if (!items) return;
    setImporting(true);
    try {
      const r = await httpClient.post<ImportResult>(endpoint, { mode: replace ? 'Replace' : 'Merge', items });
      setResult(r.data);
      const errorCount = r.data.errors?.length ?? 0;
      notify(t('common.importDialog.summary', {
        created: r.data.created, updated: r.data.updated, skipped: r.data.skipped,
        deleted: r.data.deleted ?? 0, errors: errorCount,
      }), { type: errorCount > 0 ? 'warning' : 'success' });
      refresh();
    } catch (e) {
      const body = e instanceof HttpError ? (e.data as { detail?: string; message?: string } | undefined) : undefined;
      notify(body?.detail || body?.message || 'common.importDialog.failed', { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const close = () => {
    if (importing) return;
    setItems(null);
    setResult(null);
    setReplace(false);
  };

  const dialog = (
    <ImportDialog
      open={items !== null}
      recordCount={items?.length ?? 0}
      replace={replace}
      onReplaceChange={setReplace}
      importing={importing}
      result={result}
      onClose={close}
      onRun={run}
    />
  );

  return { start, importing, dialog };
};
